import { ReactNode } from "react";
import { ArrowRight, Activity, AlertTriangle, Workflow } from "lucide-react";
import { InstLayout, Container, Eyebrow, SectionHeading, SignalCard, TrustBar } from "./InstLayout";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

export interface AudienceTemplateProps {
  seoTitle: string;
  seoDescription: string;
  canonicalUrl: string;
  eyebrow: string;
  h1: ReactNode;
  intro: ReactNode;
  painPoints: { title: string; description: string }[];
  signals: { icon: "engagement" | "risk" | "workflow"; title: string; description: string }[];
  useCases: { href: string; title: string; description: string }[];
  trustNote: string;
  ctaTitle: string;
  ctaDescription: string;
}

const ICON_MAP = { engagement: Activity, risk: AlertTriangle, workflow: Workflow };

export const AudienceTemplate = (props: AudienceTemplateProps) => (
  <InstLayout>
    <SEO
      title={props.seoTitle}
      description={props.seoDescription}
      canonicalUrl={props.canonicalUrl}
    />
    <Container>
      <header className="max-w-3xl mb-16">
        <Eyebrow>{props.eyebrow}</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
          {props.h1}
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">{props.intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
            <a href="/demo/">Request a demo<ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
          <Button asChild size="lg" variant="outline"><a href="/platform/">How the platform works</a></Button>
        </div>
      </header>

      <section className="mb-20">
        <SectionHeading eyebrow="The visibility gap" title="What's hard to see today." />
        <div className="grid md:grid-cols-3 gap-6">
          {props.painPoints.map((p) => (
            <article key={p.title} className="rounded-2xl border border-border bg-card p-7">
              <h3 className="font-display text-xl font-semibold mb-2">{p.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{p.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mb-20">
        <SectionHeading
          eyebrow="What CSW surfaces"
          title="Three layers of behavioral signal."
          description="Every signal is explainable, attributable to specific evidence, and intended for human review."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {props.signals.map((s) => (
            <SignalCard key={s.title} icon={ICON_MAP[s.icon]} title={s.title} description={s.description} />
          ))}
        </div>
      </section>

      <TrustBar note={props.trustNote} />

      <section className="mb-20">
        <SectionHeading eyebrow="Relevant use cases" title="How teams put CSW to work." />
        <div className="grid sm:grid-cols-2 gap-5">
          {props.useCases.map((u) => (
            <a key={u.href} href={u.href} className="rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition-colors group">
              <h3 className="font-display text-lg font-semibold mb-2">{u.title}</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">{u.description}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                Read use case <ArrowRight className="w-4 h-4" />
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-muted/30 p-10 md:p-14 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{props.ctaTitle}</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">{props.ctaDescription}</p>
        <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base">
          <a href="/demo/">Request a demo</a>
        </Button>
      </section>
    </Container>
  </InstLayout>
);
